import { useCallback } from 'react';

import { useCheckoutContext } from '@zooplus/checkout-state';
import {
  trackEvent as trackEventUsingLibrary,
  TrackingData,
  TrackingPage,
} from '@zooplus/tracker-listener';

import { logError } from '@services/sentry-service';
import {
  CHECKOUT_STATE_PROJECT_ID,
  AREA_TITLE,
  ADOBE_TRACKING,
  TRACKING_DATA_EVENT,
  TRACKING_PAGE,
  TARGET_ACTION_ID,
  TARGET_COMPONENT_NAME,
} from '@utilities/constants';
import { extractCheckoutDetails, getCommonTrackingData } from '@utilities/data-tracking';

import useAdobeTracking from './use-adobe-tracking';

const usePageTracking = () => {
  const { trackEvent } = useAdobeTracking();
  const { checkoutState } = useCheckoutContext(CHECKOUT_STATE_PROJECT_ID);
  const { CLICK_EVENT_GENERIC, EMPTY_CART_PAGE_IDENTIFIER, PAGE_REFRESH } = ADOBE_TRACKING;

  const checkoutDetails = checkoutState ? extractCheckoutDetails(checkoutState) : null;
  const { sid, id, guest, tnc } = checkoutDetails || {};

  const trackPageViewUsingTrackerListener = useCallback(
    (page: TrackingPage) => {
      try {
        trackEventUsingLibrary({
          event: TRACKING_DATA_EVENT.PAGE_VIEW,
          page: page,
          detail: {
            sid,
            ...getCommonTrackingData(),
            customer: {
              id,
              guest,
              tnc,
            },
          },
        } as TrackingData);
      } catch (err) {
        logError(
          new Error(
            `An error occurred while trying to PaGE_VIEW event via @zooplus/tracker-listener: ${err}`,
          ),
        );
      }
    },
    [guest, id, sid, tnc],
  );

  const trackEventUsingTrackerListener = useCallback(
    (
      eventName: TRACKING_DATA_EVENT,
      targetActionId?: TARGET_ACTION_ID,
      targetComponentName?: TARGET_COMPONENT_NAME,
    ) => {
      try {
        const pathName = window.location.pathname as keyof typeof TRACKING_PAGE;
        const customerData = {
          id,
          guest,
          tnc,
        };

        const trackingPayload = {
          event: eventName,
          page: TRACKING_PAGE[pathName] ?? 'unknown',
          detail: {
            sid,
            ...getCommonTrackingData(),
            ...(id ? { customer: customerData } : {}),
            ...(targetActionId
              ? { target: { id: targetActionId, name: targetComponentName } }
              : {}),
          },
        } as TrackingData;

        trackEventUsingLibrary(trackingPayload);
      } catch (error) {
        logError(new Error(`Error tracking event "${eventName}": ${error}`));
      }
    },
    [id, guest, tnc, sid],
  );

  const trackPageRefreshButtonEvent = () => {
    trackEvent(CLICK_EVENT_GENERIC, PAGE_REFRESH, {
      area: AREA_TITLE,
      identifier: EMPTY_CART_PAGE_IDENTIFIER,
      targetLink: window.location.href,
    });
  };

  return {
    trackPageRefreshButtonEvent,
    trackPageViewUsingTrackerListener,
    trackEventUsingTrackerListener,
  };
};

export default usePageTracking;
